import type { AppVisibility } from '@api/apps';
import type { UserAppDraft } from '@api/appBuilder';
import { useAppBuilderActions, usePublicLink } from '../useAppBuilder';

export const SHARE_PRIVATE = 'private' as const;
export const SHARE_ORG = 'org' as const;
export const SHARE_PUBLIC = 'public' as const;
export type ShareLevel = typeof SHARE_PRIVATE | typeof SHARE_ORG | typeof SHARE_PUBLIC;

export const shareLevelOf = (visibility: AppVisibility, linkToken: string | null): ShareLevel => {
  if (linkToken) return SHARE_PUBLIC;
  return visibility === 'ORG' ? SHARE_ORG : SHARE_PRIVATE;
};

/** Public keeps the app shared with the organisation as well as giving it a link. */
export const useShareLevel = (draft: UserAppDraft, open: boolean) => {
  const { appKey } = draft;
  const link = usePublicLink(appKey, open);
  const actions = useAppBuilderActions();
  const token = link.data?.token ?? null;
  const level = shareLevelOf(draft.visibility, token);

  const choose = async (next: ShareLevel) => {
    if (next === level) return;
    if (next !== SHARE_PUBLIC && token) await actions.revokePublicLink(appKey);
    const visibility: AppVisibility = next === SHARE_PRIVATE ? 'PRIVATE' : 'ORG';
    if (draft.visibility !== visibility) await actions.setVisibility({ appKey, visibility });
    if (next === SHARE_PUBLIC) await actions.createPublicLink(appKey);
  };

  return {
    level,
    link,
    choose,
    newLink: () => actions.createPublicLink(appKey),
    busy: actions.isChangingPublicLink || actions.isChangingVisibility,
    publicProblems: link.data?.problems ?? [],
  };
};
